import { FormValidation } from "@rjsf/utils";

import {
  CreateGUFAddRealisedInstallationTask,
  CreateGUFClosureRealisedPartTask,
  CreateGUFClosureTask,
  CreateGUFExpandedRealisedInstallationTask,
  CreateGUFHeightTask,
  CreateGUFNewLicenceTask,
  CreateGUFStartRegistrationTask,
  CreateGUFWellFunctionTask,
  EditGUFAddRealisedInstallationTask,
  EditGUFClosureRealisedPartTask,
  EditGUFClosureTask,
  EditGUFExpandedRealisedInstallationTask,
  EditGUFHeightTask,
  EditGUFNewLicenceTask,
  EditGUFStartRegistrationTask,
  EditGUFWellFunctionTask,
} from "@nens/brostar/uploadapi-client";

import { parseNumber } from "@/helpers/parse-number";

import { isEnergyInstallationFunction } from "./guf-installation-function-helpers";

/**
 * Custom validation for GUF StartRegistration forms.
 * Validates field formats and cross-field rules that cannot be expressed in JSON Schema.
 */
export const validateGufStartRegistrationForm = (
  formData: CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask | undefined,
  errors: FormValidation<CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask>
): FormValidation<CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const { sourcedocumentData } = formData;
  const sdErrors: Record<string, unknown> = errors.sourcedocumentData ?? {};

  // Helper to add error at a nested field path, e.g. "designInstallations.0.designWells.0.height"
  const addFieldError = (field: string, message: string) => {
    const parts = field.split(".");
    let current: unknown = sdErrors;
    for (const part of parts) {
      if (!current || typeof current !== "object") return;
      current = current[part as keyof typeof current];
    }
    if (current && typeof current === "object" && "addError" in current) {
      const errorObj = current as { addError: (msg: string) => void };
      if (typeof errorObj.addError === "function") {
        errorObj.addError(message);
      }
    }
  };

  // startTime: must match YYYY-MM-DD, YYYY-MM, or YYYY
  if (sourcedocumentData.startTime) {
    const validFormats = [/^\d{4}-\d{2}-\d{2}$/, /^\d{4}-\d{2}$/, /^\d{4}$/];
    const isValid = validFormats.some((pattern) => pattern.test(sourcedocumentData.startTime));
    if (!isValid) {
      addFieldError("startTime", "Moet het formaat JJJJ-MM-DD, JJJJ-MM of JJJJ hebben");
    }
  }

  // Validate installationPos: must contain x and y coordinates
  if (Array.isArray(sourcedocumentData.designInstallations)) {
    sourcedocumentData.designInstallations.forEach((installation, installIdx) => {
      if (
        installation.energyCharacteristics &&
        !isEnergyInstallationFunction(installation.installationFunction)
      ) {
        addFieldError(
          `designInstallations.${installIdx}.energyCharacteristics`,
          "Energiekarakteristieken is alleen toegestaan voor een open of gesloten bodemenergiesysteem"
        );
      }

      if (installation.designInstallationPos) {
        const coords = installation.designInstallationPos.trim().split(/\s+/);
        if (
          coords.length !== 2 ||
          Number.isNaN(parseNumber(coords[0])) ||
          Number.isNaN(parseNumber(coords[1]))
        ) {
          addFieldError(
            `designInstallations.${installIdx}.designInstallationPos`,
            "Moet twee coördinaten bevatten (x y)"
          );
        }
      }

      // Validate wells within installation
      if (Array.isArray(installation.designWells)) {
        installation.designWells.forEach((well, wellIdx) => {
          // wellPos: must contain x and y coordinates
          if (well.wellPos) {
            const coords = well.wellPos.trim().split(/\s+/);
            if (
              coords.length !== 2 ||
              Number.isNaN(parseNumber(coords[0])) ||
              Number.isNaN(parseNumber(coords[1]))
            ) {
              addFieldError(
                `designInstallations.${installIdx}.designWells.${wellIdx}.wellPos`,
                "Moet twee coördinaten bevatten (x y)"
              );
            }
          }

          // designScreen: designScreenBottom is required when screenType is "verticaal"
          if (
            well.designScreen?.screenType === "verticaal" &&
            !well.designScreen?.designScreenBottom
          ) {
            addFieldError(
              `designInstallations.${installIdx}.designWells.${wellIdx}.designScreen.designScreenBottom`,
              "Filterbodem is verplicht voor een verticaal filter"
            );
          }

          // Loop dates: endDate must be after startDate
          if (Array.isArray(installation.designLoops)) {
            installation.designLoops.forEach((loop, loopIdx) => {
              if (loop.startDate && loop.endDate && loop.endDate <= loop.startDate) {
                addFieldError(
                  `designInstallations.${installIdx}.designLoops.${loopIdx}.endDate`,
                  "Einddatum moet na de begindatum zijn"
                );
              }
            });
          }
        });
      }
    });
  }

  return errors;
};

// ── Shared date format validator ──────────────────────────────────────────────

const DATE_FORMATS = [/^\d{4}-\d{2}-\d{2}$/, /^\d{4}-\d{2}$/, /^\d{4}$/];
const isValidDateFormat = (value: string) => DATE_FORMATS.some((p) => p.test(value));
const DATE_FORMAT_MSG = "Moet het formaat JJJJ-MM-DD, JJJJ-MM of JJJJ hebben";

// ── GUF NewLicence ────────────────────────────────────────────────────────────

export const validateGufNewLicenceForm = (
  formData: CreateGUFNewLicenceTask | EditGUFNewLicenceTask | undefined,
  errors: FormValidation<CreateGUFNewLicenceTask | EditGUFNewLicenceTask>
): FormValidation<CreateGUFNewLicenceTask | EditGUFNewLicenceTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const sd = formData.sourcedocumentData;
  if (sd.startTime && !isValidDateFormat(sd.startTime)) {
    const sdErrors = errors.sourcedocumentData as Record<
      string,
      { addError?: (m: string) => void }
    >;
    sdErrors.startTime?.addError?.(DATE_FORMAT_MSG);
  }
  return errors;
};

// ── GUF AddRealisedInstallation / ExpandedRealisedInstallation ────────────────

const validateRealisedInstallationForm = <
  T extends
    | CreateGUFAddRealisedInstallationTask
    | EditGUFAddRealisedInstallationTask
    | CreateGUFExpandedRealisedInstallationTask
    | EditGUFExpandedRealisedInstallationTask
>(
  formData: T | undefined,
  errors: FormValidation<T>
): FormValidation<T> => {
  if (!formData?.sourcedocumentData) return errors;
  const sd = formData.sourcedocumentData;
  const sdErrors = errors.sourcedocumentData as Record<string, unknown>;

  if (sd.startValidity && !isValidDateFormat(sd.startValidity)) {
    (sdErrors.startValidity as { addError?: (m: string) => void })?.addError?.(DATE_FORMAT_MSG);
  }

  if (sd.realisedLoopPos) {
    const coords = sd.realisedLoopPos.trim().split(/\s+/);
    if (coords.length !== 2 || coords.some((c) => Number.isNaN(Number(c)))) {
      (sdErrors.realisedLoopPos as { addError?: (m: string) => void })?.addError?.(
        "Moet twee coördinaten bevatten (x y)"
      );
    }
  }

  if (Array.isArray(sd.realisedWells)) {
    sd.realisedWells.forEach((well, wellIdx) => {
      if (well.wellFunctions && well.wellFunctions.length > 2) {
        const wellErrors = (
          (sdErrors.realisedWells as Record<number, unknown>)?.[wellIdx] as Record<string, unknown>
        )?.wellFunctions as { addError?: (m: string) => void } | undefined;
        wellErrors?.addError?.("Maximaal 2 putfuncties toegestaan");
      }
    });
  }

  return errors;
};

export const validateGufAddRealisedInstallationForm = (
  formData: CreateGUFAddRealisedInstallationTask | EditGUFAddRealisedInstallationTask | undefined,
  errors: FormValidation<CreateGUFAddRealisedInstallationTask | EditGUFAddRealisedInstallationTask>
) => validateRealisedInstallationForm(formData, errors);

export const validateGufExpandedRealisedInstallationForm = (
  formData:
    | CreateGUFExpandedRealisedInstallationTask
    | EditGUFExpandedRealisedInstallationTask
    | undefined,
  errors: FormValidation<
    CreateGUFExpandedRealisedInstallationTask | EditGUFExpandedRealisedInstallationTask
  >
) => validateRealisedInstallationForm(formData, errors);

// ── GUF Height ────────────────────────────────────────────────────────────────

export const validateGufHeightForm = (
  formData: CreateGUFHeightTask | EditGUFHeightTask | undefined,
  errors: FormValidation<CreateGUFHeightTask | EditGUFHeightTask>
): FormValidation<CreateGUFHeightTask | EditGUFHeightTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const sd = formData.sourcedocumentData;
  if (sd.startValidity && !isValidDateFormat(sd.startValidity)) {
    (
      errors.sourcedocumentData as Record<string, { addError?: (m: string) => void }>
    ).startValidity?.addError?.(DATE_FORMAT_MSG);
  }
  return errors;
};

// ── GUF WellFunction ──────────────────────────────────────────────────────────

export const validateGufWellFunctionForm = (
  formData: CreateGUFWellFunctionTask | EditGUFWellFunctionTask | undefined,
  errors: FormValidation<CreateGUFWellFunctionTask | EditGUFWellFunctionTask>
): FormValidation<CreateGUFWellFunctionTask | EditGUFWellFunctionTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const sd = formData.sourcedocumentData;
  const sdErrors = errors.sourcedocumentData as Record<string, unknown>;

  if (sd.eventDate && !isValidDateFormat(sd.eventDate)) {
    (sdErrors.eventDate as { addError?: (m: string) => void })?.addError?.(DATE_FORMAT_MSG);
  }
  if (sd.wellFunctions && sd.wellFunctions.length > 2) {
    (sdErrors.wellFunctions as { addError?: (m: string) => void })?.addError?.(
      "Maximaal 2 putfuncties toegestaan"
    );
  }
  return errors;
};

// ── GUF ClosureRealisedPart ───────────────────────────────────────────────────

export const validateGufClosureRealisedPartForm = (
  formData: CreateGUFClosureRealisedPartTask | EditGUFClosureRealisedPartTask | undefined,
  errors: FormValidation<CreateGUFClosureRealisedPartTask | EditGUFClosureRealisedPartTask>
): FormValidation<CreateGUFClosureRealisedPartTask | EditGUFClosureRealisedPartTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const sd = formData.sourcedocumentData;
  if (sd.endTime && !isValidDateFormat(sd.endTime)) {
    (
      errors.sourcedocumentData as Record<string, { addError?: (m: string) => void }>
    ).endTime?.addError?.(DATE_FORMAT_MSG);
  }
  return errors;
};

// ── GUF Closure ───────────────────────────────────────────────────────────────

export const validateGufClosureForm = (
  formData: CreateGUFClosureTask | EditGUFClosureTask | undefined,
  errors: FormValidation<CreateGUFClosureTask | EditGUFClosureTask>
): FormValidation<CreateGUFClosureTask | EditGUFClosureTask> => {
  if (!formData?.sourcedocumentData) return errors;
  const sd = formData.sourcedocumentData;
  if (sd.endTime && !isValidDateFormat(sd.endTime)) {
    (
      errors.sourcedocumentData as Record<string, { addError?: (m: string) => void }>
    ).endTime?.addError?.(DATE_FORMAT_MSG);
  }
  return errors;
};
